import * as React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Checkbox } from "../ui/Checkbox";
import { Icon } from "../ui/Icon";
import { BookingFields } from "./BookingFields";
import {
  BOOKING_FORMS,
  SERVICE_SHAPE,
  splitPayload,
  validateBooking,
  visibleSpecs,
} from "./bookingConfig";
import type { FormValues } from "./bookingConfig";
import {
  useBookingCheckoutMutation,
  useBookingInquiryMutation,
  useCalendlyStartMutation,
} from "../../hooks/useBookings";
import type { ServiceContent } from "../../api/content";
import { MarkdownBody } from "../scene/MarkdownBody";

type Status = "form" | "inquiry-success" | "paid-success";

type Props = {
  service: ServiceContent | null;
  open: boolean;
  onClose: () => void;
  portalHost?: HTMLDivElement | null;
};

export function BookingModal({
  service,
  open,
  onClose,
  portalHost,
}: Props) {
  // Default ns (`common`) carries `thankYou` / `done` / `somethingWrong`;
  // booking-specific copy lives in the `booking` namespace.
  const { t } = useTranslation();
  const { t: tb } = useTranslation("booking");
  const shape = service ? SERVICE_SHAPE[service.key] : undefined;
  const specs = shape ? BOOKING_FORMS[shape] : [];
  const [values, setValues] = React.useState<FormValues>({});
  const [agreed, setAgreed] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);
  const [status, setStatus] = React.useState<Status>("form");
  const inquiry = useBookingInquiryMutation();
  const checkout = useBookingCheckoutMutation();
  const calendlyStart = useCalendlyStartMutation();

  React.useEffect(() => {
    if (open) {
      setValues({});
      setAgreed(false);
      setShowErrors(false);
      setStatus("form");
    }
  }, [open, service?.key]);

  if (!service) return null;

  const errors = validateBooking(specs, values);
  const valid = Object.values(errors).every((e) => !e) && agreed;
  const pending =
    inquiry.isPending || checkout.isPending || calendlyStart.isPending;
  const isSuccess = status !== "form";

  // Only submit values for fields currently visible — a field hidden by a
  // revealWhen toggle (e.g. preferredDate after switching off "Specific Date")
  // must not ride along with a stale value.
  const buildPayload = () => {
    const visible: FormValues = {};
    for (const spec of visibleSpecs(specs, values)) {
      visible[spec.key] = values[spec.key] ?? "";
    }
    return { serviceKey: service.key, ...splitPayload(visible) };
  };

  const guard = (run: () => void) => {
    if (pending) return;
    if (!valid) {
      setShowErrors(true);
      return;
    }
    run();
  };

  // Calendly services: Book silently pre-records the request (no owner
  // notification yet), then redirects to Calendly where scheduling + payment
  // happen; the backend webhook flips the record to paid and notifies. The
  // record id rides in utm_content so the webhook can match it. If the
  // pre-record call fails, still redirect — never block the booking.
  const isCalendly = Boolean(service.calendlyUrl);

  const calendlyHref = (bookingId?: string) => {
    const url = new URL(service.calendlyUrl);
    const name = (values.fullName ?? "").trim();
    const email = (values.email ?? "").trim();
    if (name) url.searchParams.set("name", name);
    if (email) url.searchParams.set("email", email);
    if (bookingId) url.searchParams.set("utm_content", bookingId);
    return url.toString();
  };

  const onInquiry = () =>
    guard(() =>
      inquiry.mutate(buildPayload(), {
        onSuccess: () => setStatus("inquiry-success"),
      }),
    );

  const onBook = () =>
    guard(() =>
      isCalendly
        ? calendlyStart.mutate(buildPayload(), {
            onSuccess: ({ id }) => {
              window.location.href = calendlyHref(id);
            },
            onError: () => {
              window.location.href = calendlyHref();
            },
          })
        : checkout.mutate(buildPayload(), {
            onSuccess: ({ url }) => {
              if (url) window.location.href = url;
              else setStatus("paid-success");
            },
          }),
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={isSuccess ? undefined : tb("eyebrow")}
      title={isSuccess ? undefined : service.title.replace(/\n/g, " ")}
      size={isSuccess ? "default" : "wide"}
      container={portalHost ?? null}
      footerLayout="full"
      footer={
        isSuccess ? undefined : (
          <div className="flex w-full flex-col gap-3 [&>button]:w-full">
            <Button
              variant="solid"
              surface="light"
              as="button"
              type="button"
              disabled={pending || !agreed}
              onClick={onBook}
            >
              {service.bookLabel}
            </Button>
            {service.inquiryLabel && !isCalendly ? (
              <Button
                variant="outline"
                surface="light"
                as="button"
                type="button"
                disabled={pending || !agreed}
                onClick={onInquiry}
              >
                {service.inquiryLabel}
              </Button>
            ) : null}
          </div>
        )
      }
    >
      {isSuccess ? (
        <div className="flex flex-col items-center gap-5 py-6 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-[22px] text-primary">
            <Icon name="verified" title={tb("bookingReceived")} />
          </span>
          <p className="t-display text-primary">{t('thankYou')}</p>
          <div className="max-w-[40ch]">
            <MarkdownBody
              surface="brand"
              body={status === "paid-success" ? service.paymentSuccess : service.inquirySuccess}
            />
          </div>
          <Button
            variant="outline"
            surface="light"
            as="button"
            type="button"
            onClick={onClose}
          >
            {t('done')}
          </Button>
        </div>
      ) : (
        <>
          <BookingFields
            specs={specs}
            values={values}
            errors={showErrors ? errors : {}}
            onChange={(key, value) =>
              setValues((prev) => ({ ...prev, [key]: value }))
            }
          />
          <div className="flex flex-col gap-2.5">
            <Checkbox
              checked={agreed}
              onChange={setAgreed}
              label={
                <>
                  {tb("agreeTermsPrefix")}{" "}
                  <a
                    href={`${process.env.PUBLIC_URL}/terms-and-conditions`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2"
                  >
                    {tb("termsLinkLabel")}
                  </a>{" "}
                  {tb("agreeTermsAnd")}{" "}
                  <a
                    href={`${process.env.PUBLIC_URL}/privacy-policy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2"
                  >
                    {tb("privacyLinkLabel")}
                  </a>
                </>
              }
            />
            {showErrors && !agreed ? (
              <span className="flex items-center gap-2 text-xs font-medium leading-snug text-error">
                <span
                  aria-hidden="true"
                  className="block h-2 w-2 flex-none rotate-45 bg-error"
                />
                {tb("agreeRequired")}
              </span>
            ) : null}
          </div>
          {(inquiry.isError || checkout.isError) && (
            <p role="alert" className="t-body-blue text-error">
              {t('somethingWrong')}
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
